import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_URL, getAuthHeader } from '../../utils/config';
import { playNotifSound, SOUND_NAMES } from '../../utils/notifSound';
import { applySettings } from '../../utils/settings';
import useStore from '../../store/useStore';

const NAV = [
  {
    id: 'appearance',
    label: 'Appearance',
    sub: 'Theme, font, wallpaper',
    color: '#f0fdfa',
    stroke: '#0d9488',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}>
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
      </svg>
    ),
  },
  {
    id: 'chat',
    label: 'Chat',
    sub: 'Messages, media, sounds',
    color: '#eff6ff',
    stroke: '#2563eb',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}>
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
      </svg>
    ),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    sub: 'Alerts, DND, sounds',
    color: '#fdf4ff',
    stroke: '#9333ea',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}>
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 01-3.46 0"/>
      </svg>
    ),
  },
  {
    id: 'privacy',
    label: 'Privacy',
    sub: 'Visibility, encryption',
    color: '#fff7ed',
    stroke: '#ea580c',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
  },
  {
    id: 'storage',
    label: 'Storage & Data',
    sub: 'Cache, media, usage',
    color: '#fef9c3',
    stroke: '#ca8a04',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}>
        <ellipse cx="12" cy="5" rx="9" ry="3"/>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
      </svg>
    ),
  },
  {
    id: 'accessibility',
    label: 'Accessibility',
    sub: 'Contrast, animations',
    color: '#f0fdf4',
    stroke: '#16a34a',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}>
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v4l3 3"/>
      </svg>
    ),
  },
  {
    id: 'about',
    label: 'About Qtalk',
    sub: 'v1.2.0',
    color: 'transparent',
    stroke: 'var(--sb-text, #888)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}>
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    ),
  },
];

const FONT_LABELS = ['Tiny', 'Small', 'Medium', 'Large', 'Extra Large'];
const FONT_SIZES  = [11, 12, 14, 16, 18];

const WALLPAPERS = [
  { id: 'none', bg: 'transparent', label: 'None' },
  { id: 'wp1', bg: '#f0fdf4', label: 'Mint' },
  { id: 'wp2', bg: 'linear-gradient(135deg,#dbeafe,#ede9fe)', label: 'Sky' },
  { id: 'wp3', bg: 'linear-gradient(135deg,#fef3c7,#fde68a)', label: 'Warm' },
  { id: 'wp4', bg: 'linear-gradient(135deg,#fce7f3,#fbcfe8)', label: 'Rose' },
  { id: 'wp5', bg: 'linear-gradient(135deg,#ecfdf5,#d1fae5)', label: 'Forest' },
  { id: 'wp6', bg: 'linear-gradient(135deg,#1e293b,#0f172a)', label: 'Dark' },
];

// ── tiny helpers ──────────────────────────────────────────
const Row = ({ label, sub, right }) => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 16px',gap:12,borderTop:'0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))'}}>
    <div style={{flex:1,minWidth:0}}>
      <span style={{display:'block',fontSize:13,fontWeight:500,color:'var(--row-text,#111)'}}>{label}</span>
      {sub && <span style={{display:'block',fontSize:11,color:'var(--row-sub,#888)',marginTop:2,lineHeight:1.4}}>{sub}</span>}
    </div>
    {right}
  </div>
);

const Toggle = ({ on, onChange }) => (
  <div onClick={() => onChange(!on)} style={{
    width:38,height:22,borderRadius:11,flexShrink:0,cursor:'pointer',position:'relative',
    transition:'background 0.2s',
    background: on ? 'linear-gradient(90deg,#0d9488,#0891b2)' : '#d1d5db'
  }}>
    <div style={{
      position:'absolute',width:16,height:16,background:'#fff',borderRadius:'50%',
      top:3, left: on ? 19 : 3, transition:'left 0.2s',
      boxShadow:'0 1px 3px rgba(0,0,0,0.2)'
    }}/>
  </div>
);

const Sel = ({ value, onChange, children }) => (
  <select value={value} onChange={e => onChange(e.target.value)} style={{
    fontSize:12,padding:'6px 28px 6px 10px',borderRadius:8,
    border:'0.5px solid rgba(0,0,0,0.15)',
    background:'var(--sel-bg,#f8fafc)',color:'var(--row-text,#111)',
    cursor:'pointer',appearance:'none',WebkitAppearance:'none',
    backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat:'no-repeat',backgroundPosition:'right 8px center'
  }}>{children}</select>
);

const Card = ({ children, style }) => (
  <div style={{
    background:'var(--card-bg,#fff)',borderRadius:12,marginBottom:18,
    border:'0.5px solid var(--color-border-tertiary,rgba(0,0,0,0.08))',
    overflow:'hidden', ...style
  }}>{children}</div>
);

const CardLabel = ({ children }) => (
  <div style={{fontSize:10.5,fontWeight:600,color:'var(--row-sub,#888)',letterSpacing:'0.06em',padding:'12px 16px 6px'}}>
    {children}
  </div>
);

const PgHeader = ({ navItem }) => (
  <div style={{display:'flex',alignItems:'center',gap:14,paddingBottom:18,borderBottom:'0.5px solid var(--color-border-tertiary,rgba(0,0,0,0.08))'}}>
    <div style={{width:42,height:42,borderRadius:12,background:navItem.color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:navItem.stroke}}>
      {React.cloneElement(navItem.icon, {style:{width:20,height:20}})}
    </div>
    <div>
      <h3 style={{fontSize:16,fontWeight:600,color:'var(--row-text,#111)',letterSpacing:'-0.3px',margin:0}}>{navItem.label}</h3>
      <p style={{fontSize:12,color:'var(--row-sub,#888)',marginTop:2}}>{navItem.sub}</p>
    </div>
  </div>
);

// ── main component ────────────────────────────────────────
const spinStyle = document.createElement('style');
spinStyle.textContent = '@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
if (!document.getElementById('qtalk-spin')) { spinStyle.id='qtalk-spin'; document.head.appendChild(spinStyle); }

export default function SettingsModal({ onClose }) {
  const { darkMode, toggleDarkMode } = useStore();

  // Load persisted settings
  const load = (key, def) => {
    try { const v = localStorage.getItem('qtalk_setting_' + key); return v !== null ? JSON.parse(v) : def; } catch { return def; }
  };
  const save = (key, val) => {
    try {
      localStorage.setItem('qtalk_setting_' + key, JSON.stringify(val));
      applySettings();
      window.dispatchEvent(new Event('qtalk-settings-changed'));
    } catch {}
  };

  const [tab,         setTab]         = useState('appearance');
  const [themeMode,   setThemeMode]   = useState(() => darkMode ? 'dark' : 'light');
  const [fontSize,    setFontSize]    = useState(() => load('fontSize', 3));
  const [wallpaper,   setWallpaper]   = useState(() => load('wallpaper', 'wp1'));
  const [bubbleStyle, setBubbleStyle] = useState(() => load('bubbleStyle', 'Rounded (default)'));
  const [timestamps,  setTimestamps]  = useState(() => load('timestamps', true));
  const [compactMode, setCompactMode] = useState(() => load('compactMode', false));

  const [disappearing,  setDisappearing]  = useState(() => load('disappearing', 'Off'));
  const [enterSends,    setEnterSends]    = useState(() => load('enterSends', true));
  const [readReceipts,  setReadReceipts]  = useState(() => load('readReceipts', true));
  const [typingInd,     setTypingInd]     = useState(() => load('typingInd', true));
  const [linkPreviews,  setLinkPreviews]  = useState(() => load('linkPreviews', true));
  const [autoDownload,  setAutoDownload]  = useState(() => load('autoDownload', true));
  const [msgSound,      setMsgSound]      = useState(() => load('msgSound', 'Default'));

  const [pushNotifs,    setPushNotifs]    = useState(() => load('pushNotifs', true));
  const [msgPreview,    setMsgPreview]    = useState(() => load('msgPreview', true));
  const [notifSound,    setNotifSound]    = useState(() => load('notifSound', 'Default'));
  const [taskAlerts,    setTaskAlerts]    = useState(() => load('taskAlerts', true));
  const [dndEnabled,    setDndEnabled]    = useState(() => load('dndEnabled', false));
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showPwd, setShowPwd] = useState({ current: false, newPwd: false, confirm: false });

  const handleChangePassword = async () => {
    if (!pwdForm.current || !pwdForm.newPwd || !pwdForm.confirm)
      return toast.error('Fill all fields');
    if (pwdForm.newPwd.length < 8)
      return toast.error('New password must be at least 8 characters');
    if (pwdForm.newPwd !== pwdForm.confirm)
      return toast.error('Passwords do not match');
    setPwdLoading(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`,
        { current_password: pwdForm.current, new_password: pwdForm.newPwd },
        { headers: getAuthHeader() }
      );
      toast.success('Password changed successfully!');
      setPwdForm({ current: '', newPwd: '', confirm: '' });
      setShowChangePwd(false);
    } catch(e) {
      toast.error(e.response?.data?.message || 'Failed to change password');
    } finally { setPwdLoading(false); }
  };

  const [lastSeen,      setLastSeen]      = useState(() => load('lastSeen', 'Everyone'));
  const [onlineVis,     setOnlineVis]     = useState(() => load('onlineVis', 'Everyone'));
  const [readReceiptsP, setReadReceiptsP] = useState(() => load('readReceiptsP', true));
  const [photoVis,      setPhotoVis]      = useState(() => load('photoVis', 'Everyone'));

  const [dataSaver,     setDataSaver]     = useState(() => load('dataSaver', false));
  const [reduceMotion,  setReduceMotion]  = useState(() => load('reduceMotion', false));
  const [highContrast,  setHighContrast]  = useState(() => load('highContrast', false));
  const [language,      setLanguage]      = useState(() => load('language', 'English'));

  // Apply font size globally
  useEffect(() => {
    save('fontSize', fontSize);
    const sizes = FONT_SIZES;
    document.documentElement.style.setProperty('--qtalk-font-size', sizes[fontSize - 1] + 'px');
  }, [fontSize]);

  // Sync theme toggle with store
  const handleTheme = (mode) => {
    setThemeMode(mode);
    if (mode === 'dark'   && !darkMode) toggleDarkMode();
    if (mode === 'light'  &&  darkMode) toggleDarkMode();
    save('themeMode', mode);
  };

  // Persist all other settings
  useEffect(() => { save('wallpaper',   wallpaper);   }, [wallpaper]);
  useEffect(() => { save('bubbleStyle', bubbleStyle); }, [bubbleStyle]);
  useEffect(() => { save('timestamps',  timestamps);  }, [timestamps]);
  useEffect(() => { save('compactMode', compactMode); }, [compactMode]);
  useEffect(() => { save('disappearing',disappearing);}, [disappearing]);
  useEffect(() => { save('enterSends',  enterSends);  }, [enterSends]);
  useEffect(() => { save('readReceipts',readReceipts);}, [readReceipts]);
  useEffect(() => { save('typingInd',   typingInd);   }, [typingInd]);
  useEffect(() => { save('linkPreviews',linkPreviews);}, [linkPreviews]);
  useEffect(() => { save('autoDownload',autoDownload);}, [autoDownload]);
  useEffect(() => { save('msgSound',    msgSound);    }, [msgSound]);
  useEffect(() => { save('pushNotifs',  pushNotifs);  }, [pushNotifs]);
  useEffect(() => { save('msgPreview',  msgPreview);  }, [msgPreview]);
  useEffect(() => { save('notifSound',  notifSound);  }, [notifSound]);
  useEffect(() => { save('taskAlerts',  taskAlerts);  }, [taskAlerts]);
  useEffect(() => { save('dndEnabled',  dndEnabled);  }, [dndEnabled]);
  useEffect(() => { save('lastSeen',    lastSeen);    }, [lastSeen]);
  useEffect(() => { save('onlineVis',   onlineVis);   }, [onlineVis]);
  useEffect(() => { save('readReceiptsP',readReceiptsP);}, [readReceiptsP]);
  useEffect(() => { save('photoVis',    photoVis);    }, [photoVis]);
  useEffect(() => { save('dataSaver',   dataSaver);   }, [dataSaver]);
  useEffect(() => { save('reduceMotion',reduceMotion);}, [reduceMotion]);
  useEffect(() => { save('highContrast',highContrast);}, [highContrast]);
  useEffect(() => { save('language',    language);    }, [language]);

  const isDark = darkMode;
  const css = {
    '--row-text':  isDark ? '#f1f5f9' : '#0f172a',
    '--row-sub':   isDark ? '#94a3b8' : '#64748b',
    '--card-bg':   isDark ? '#1e293b' : '#ffffff',
    '--sel-bg':    isDark ? '#0f172a' : '#f8fafc',
    '--color-border-tertiary': isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
  };

  const activeNav = NAV.find(n => n.id === tab);

  // ── Theme preview mini-cards ──
  const ThemeOpt = ({ id, label, children }) => (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6,cursor:'pointer'}} onClick={() => handleTheme(id)}>
      <div style={{
        width:78,height:58,borderRadius:10,overflow:'hidden',
        border: themeMode === id ? '2px solid #0d9488' : '1.5px solid rgba(0,0,0,0.1)',
        boxShadow: themeMode === id ? '0 0 0 3px rgba(13,148,136,0.15)' : 'none',
        transition:'all 0.15s',
      }}>{children}</div>
      <span style={{fontSize:11,fontWeight:500,color:'var(--row-sub)'}}>{label}</span>
    </div>
  );

  const WpOpt = ({ wp }) => (
    <div onClick={() => { setWallpaper(wp.id); save('wallpaperCustom', ''); }} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,cursor:'pointer'}}>
      <div style={{
        width:46,height:34,borderRadius:8,flexShrink:0,
        background: wp.bg || (isDark ? '#1e293b' : '#f8fafc'),
        border: wallpaper === wp.id ? '2px solid #0d9488' : '1.5px solid rgba(0,0,0,0.1)',
        boxShadow: wallpaper === wp.id ? '0 0 0 3px rgba(13,148,136,0.15)' : 'none',
        transition:'all 0.15s',
        display:'flex',alignItems:'center',justifyContent:'center',
      }}>
        {wp.id === 'none' && <span style={{fontSize:16,opacity:0.3}}>✕</span>}
      </div>
      <span style={{fontSize:9,color:'var(--row-sub)',fontWeight:500}}>{wp.label}</span>
    </div>
  );

  return (
    <div style={{
      position:'fixed',inset:0,zIndex:9999,
      display:'flex',alignItems:'center',justifyContent:'center',
      background:'rgba(0,0,0,0.45)',
      backdropFilter:'blur(4px)',
    }} onClick={e => e.target === e.currentTarget && onClose()}>

      <div style={{
        display:'flex',alignItems:'stretch',width:'min(780px,96vw)',height:'min(580px,92vh)',maxHeight:'92vh',
        borderRadius:18,overflow:'hidden',

        boxShadow:'0 24px 80px rgba(0,0,0,0.22)',
        border:'0.5px solid rgba(255,255,255,0.1)',
        ...css,
      }}>

        {/* ── SIDEBAR ── */}
        <div style={{
          width:220,flexShrink:0,
          background: isDark ? '#0f172a' : '#f8fafc',
          borderRight:`0.5px solid ${isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.07)'}`,
          display:'flex',flexDirection:'column',
        }}>
          {/* Header */}
          <div style={{padding:'22px 20px 14px',borderBottom:`0.5px solid ${isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.07)'}`}}>
            <div style={{fontSize:15,fontWeight:700,color:'var(--row-text)',letterSpacing:'-0.3px'}}>Settings</div>
            <div style={{fontSize:11,color:'var(--row-sub)',marginTop:2}}>Qtalk preferences</div>
          </div>

          {/* Nav items */}
          <div style={{flex:1,padding:'10px 8px',overflowY:'auto',display:'flex',flexDirection:'column',gap:1}}>
            {NAV.filter(n => n.id !== 'about').map(n => (
              <div key={n.id} onClick={() => setTab(n.id)} style={{
                display:'flex',alignItems:'center',gap:11,padding:'9px 12px',
                borderRadius:10,cursor:'pointer',position:'relative',
                background: tab === n.id ? (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)') : 'transparent',
                transition:'background 0.12s',
              }}>
                {tab === n.id && (
                  <div style={{position:'absolute',left:0,top:6,bottom:6,width:3,borderRadius:'0 3px 3px 0',background:'linear-gradient(180deg,#0d9488,#2563eb)'}}/>
                )}
                <div style={{width:32,height:32,borderRadius:9,background:n.color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:n.stroke}}>
                  {n.icon}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <span style={{display:'block',fontSize:13,fontWeight:500,color: tab===n.id ? '#0d9488' : 'var(--row-text)'}}>{n.label}</span>
                  <span style={{display:'block',fontSize:10.5,color:'var(--row-sub)',marginTop:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{n.sub}</span>
                </div>
              </div>
            ))}
          </div>

          {/* About at bottom */}
          <div style={{padding:'8px',borderTop:`0.5px solid ${isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.07)'}`}}>
            {NAV.filter(n => n.id === 'about').map(n => (
              <div key={n.id} onClick={() => setTab(n.id)} style={{
                display:'flex',alignItems:'center',gap:11,padding:'9px 12px',
                borderRadius:10,cursor:'pointer',
                background: tab === n.id ? (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)') : 'transparent',
              }}>
                <div style={{width:32,height:32,borderRadius:9,background:isDark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.05)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:'var(--row-sub)'}}>
                  {n.icon}
                </div>
                <div>
                  <span style={{display:'block',fontSize:13,fontWeight:500,color:'var(--row-text)'}}>{n.label}</span>
                  <span style={{display:'block',fontSize:10.5,color:'var(--row-sub)',marginTop:1}}>{n.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CONTENT ── */}
        <div style={{flex:1,minHeight:0,minWidth:0,overflowY:'auto',height:'min(580px,92vh)',maxHeight:'min(580px,92vh)',background: isDark ? '#1e293b' : '#f1f5f9',padding:'24px'}}>

          {activeNav && <PgHeader navItem={activeNav}/>}

          {/* ═══ APPEARANCE ═══ */}
          {tab === 'appearance' && <>
            <Card style={{overflow:'visible'}}>
              <CardLabel>THEME</CardLabel>
              <div style={{display:'flex',gap:14,padding:'10px 18px 18px'}}>
                <ThemeOpt id="light" label="Light">
                  <div style={{height:'100%',background:'#f8fafc',display:'flex',flexDirection:'column'}}>
                    <div style={{height:13,background:'#fff',borderBottom:'0.5px solid #e2e8f0',display:'flex',alignItems:'center',padding:'0 5px',gap:3}}>
                      <div style={{width:5,height:5,borderRadius:'50%',background:'#0d9488'}}/>
                    </div>
                    <div style={{display:'flex',flex:1}}>
                      <div style={{width:18,background:'#f1f5f9'}}/>
                      <div style={{flex:1,display:'flex',flexDirection:'column',gap:3,padding:'4px 4px 4px 3px'}}>
                        <div style={{height:5,borderRadius:3,background:'#e2e8f0',width:'70%'}}/>
                        <div style={{height:5,borderRadius:3,background:'#0d9488',width:'55%',alignSelf:'flex-end'}}/>
                      </div>
                    </div>
                  </div>
                </ThemeOpt>
                <ThemeOpt id="dark" label="Dark">
                  <div style={{height:'100%',background:'#0f172a',display:'flex',flexDirection:'column'}}>
                    <div style={{height:13,background:'#1e293b',borderBottom:'0.5px solid #334155',display:'flex',alignItems:'center',padding:'0 5px',gap:3}}>
                      <div style={{width:5,height:5,borderRadius:'50%',background:'#0d9488'}}/>
                    </div>
                    <div style={{display:'flex',flex:1}}>
                      <div style={{width:18,background:'#1e293b'}}/>
                      <div style={{flex:1,display:'flex',flexDirection:'column',gap:3,padding:'4px 4px 4px 3px'}}>
                        <div style={{height:5,borderRadius:3,background:'#334155',width:'70%'}}/>
                        <div style={{height:5,borderRadius:3,background:'#0d9488',width:'55%',alignSelf:'flex-end'}}/>
                      </div>
                    </div>
                  </div>
                </ThemeOpt>
              </div>
            </Card>

            <Card>
              <CardLabel>FONT SIZE</CardLabel>
              <div style={{padding:'6px 16px 16px',display:'flex',flexDirection:'column',gap:8}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:11,color:'var(--row-sub)'}}>A</span>
                  <input type="range" min="1" max="5" value={fontSize} onChange={e => setFontSize(Number(e.target.value))}
                    style={{flex:1,accentColor:'#0d9488',cursor:'pointer'}}/>
                  <span style={{fontSize:17,fontWeight:500,color:'var(--row-sub)'}}>A</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between'}}>
                  {FONT_LABELS.map(l => <span key={l} style={{fontSize:10,color:'var(--row-sub)'}}>{l}</span>)}
                </div>
                <div style={{fontSize:FONT_SIZES[fontSize-1],color:'var(--row-sub)',textAlign:'center',marginTop:4,transition:'font-size 0.15s',padding:'8px 0',borderTop:`0.5px solid ${isDark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)'}`}}>
                  "{FONT_LABELS[fontSize-1]}" — The quick brown fox jumps over the lazy dog
                </div>
              </div>
            </Card>

            <Card>
              <CardLabel>CHAT WALLPAPER</CardLabel>
              <div style={{display:'flex',gap:8,padding:'6px 16px 16px',flexWrap:'wrap'}}>
                {WALLPAPERS.map(wp => <WpOpt key={wp.id} wp={wp}/>)}
                {/* Custom wallpaper */}
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,cursor:'pointer'}}>
                  <label style={{
                    width:46,height:34,borderRadius:8,flexShrink:0,cursor:'pointer',
                    background: isDark ? '#1e293b' : '#f8fafc',
                    border: '1.5px dashed #0d9488',
                    display:'flex',alignItems:'center',justifyContent:'center',
                    transition:'all 0.15s',
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}>
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <input type="file" accept="image/*" style={{display:'none'}} onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => {
                        save('wallpaperCustom', ev.target.result);
                        save('wallpaper', 'custom');
                        setWallpaper('custom');
                      };
                      reader.readAsDataURL(file);
                    }}/>
                  </label>
                  <span style={{fontSize:9,color:'var(--row-sub)',fontWeight:500}}>Custom</span>
                </div>
                <div style={{
                  width:46,height:34,borderRadius:8,flexShrink:0,cursor:'pointer',
                  border:`1.5px dashed ${isDark?'rgba(255,255,255,0.2)':'rgba(0,0,0,0.2)'}`,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  color:'var(--row-sub)',fontSize:18,
                }}>+</div>
              </div>
            </Card>

            <Card>
              <Row label="Bubble style" right={<Sel value={bubbleStyle} onChange={setBubbleStyle}><option>Rounded (default)</option><option>Sharp corners</option><option>Minimal / flat</option></Sel>}/>
              <Row label="Show timestamps" sub="Display time below each message" right={<Toggle on={timestamps} onChange={setTimestamps}/>}/>
              <Row label="Compact mode" sub="Reduce spacing between messages" right={<Toggle on={compactMode} onChange={setCompactMode}/>}/>
            </Card>
          </>}

          {/* ═══ CHAT ═══ */}
          {tab === 'chat' && <>
            <Card>
              <CardLabel>DISAPPEARING MESSAGES</CardLabel>
              <Row label="Default timer" sub="Auto-delete messages after this duration"
                right={<Sel value={disappearing} onChange={setDisappearing}><option>Off</option><option>24 hours</option><option>7 days</option><option>30 days</option><option>90 days</option></Sel>}/>
            </Card>
            <Card>
              <Row label="Enter key sends message"  sub="Shift+Enter for new line"             right={<Toggle on={enterSends}   onChange={setEnterSends}/>}/>
              <Row label="Show read receipts"        sub="Let others see when you've read"      right={<Toggle on={readReceipts} onChange={setReadReceipts}/>}/>
              <Row label="Typing indicator"          sub="Show when you're composing"           right={<Toggle on={typingInd}    onChange={setTypingInd}/>}/>
              <Row label="Link previews"             sub="Show rich previews for URLs"          right={<Toggle on={linkPreviews} onChange={setLinkPreviews}/>}/>
              <Row label="Auto-download images"      sub="On Wi-Fi only"                        right={<Toggle on={autoDownload} onChange={setAutoDownload}/>}/>
              <Row label="Message sound"
                right={
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <Sel value={msgSound} onChange={(v) => { setMsgSound(v); setTimeout(() => playNotifSound(v), 100); }}>
                      {SOUND_NAMES.map(s => <option key={s}>{s}</option>)}
                    </Sel>
                    <button onClick={() => playNotifSound(msgSound)} style={{
                      fontSize:11,fontWeight:600,padding:'5px 12px',borderRadius:8,
                      border:'none',background:'linear-gradient(90deg,#0d9488,#0891b2)',
                      color:'#fff',cursor:'pointer',whiteSpace:'nowrap',flexShrink:0
                    }}>▶ Test</button>
                  </div>
                }/>
            </Card>
          </>}

          {/* ═══ NOTIFICATIONS ═══ */}
          {tab === 'notifications' && <>
            <Card>
              <Row label="Push notifications"    sub="Receive alerts when app is in background"  right={<Toggle on={pushNotifs}  onChange={setPushNotifs}/>}/>
              <Row label="Message preview"       sub="Show content in notification banner"        right={<Toggle on={msgPreview}  onChange={setMsgPreview}/>}/>
              <Row label="Notification sound"
                right={
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <Sel value={notifSound} onChange={(v) => { setNotifSound(v); setTimeout(() => playNotifSound(v), 100); }}>
                      {SOUND_NAMES.map(s => <option key={s}>{s}</option>)}
                    </Sel>
                    <button onClick={() => playNotifSound(notifSound)} style={{
                      fontSize:11,fontWeight:600,padding:'5px 12px',borderRadius:8,
                      border:'none',background:'linear-gradient(90deg,#0d9488,#0891b2)',
                      color:'#fff',cursor:'pointer',whiteSpace:'nowrap',flexShrink:0
                    }}>▶ Test</button>
                  </div>
                }/>
              <Row label="Task reminder alerts"  sub="30-min warning before due tasks"            right={<Toggle on={taskAlerts}  onChange={setTaskAlerts}/>}/>
            </Card>
            <Card>
              <CardLabel>DO NOT DISTURB</CardLabel>
              <Row label="Enable DND schedule" sub="Silence all notifications automatically"  right={<Toggle on={dndEnabled} onChange={setDndEnabled}/>}/>
              <Row label="Quiet hours" right={<span style={{fontSize:12,fontWeight:500,color:'var(--row-sub)'}}>10 PM – 8 AM</span>}/>
            </Card>
          </>}

          {/* ═══ PRIVACY ═══ */}
          {tab === 'privacy' && <>
            {/* Change Password */}
            <Card>
              <Row label="Change Password" sub="Update your login password"
                right={<button style={{...btnStyle(isDark)}} onClick={() => setShowChangePwd(true)}>Change</button>}/>
            </Card>

            {/* Change Password Modal */}
            {showChangePwd && (
              <div style={{position:'fixed',inset:0,zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.6)',backdropFilter:'blur(8px)'}}>
                <div style={{
                  width:'100%',maxWidth:400,margin:'0 16px',borderRadius:24,
                  background: isDark ? '#1e293b' : '#fff',
                  boxShadow:'0 32px 80px rgba(0,0,0,0.4)',
                  border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                  overflow:'hidden'
                }}>
                  {/* Header */}
                  <div style={{padding:'24px 24px 0',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{display:'flex',alignItems:'center',gap:12}}>
                      <div style={{width:40,height:40,borderRadius:12,background:'linear-gradient(135deg,#0d9488,#2563eb)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:20,height:20}}>
                          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                        </svg>
                      </div>
                      <div>
                        <h3 style={{margin:0,fontSize:16,fontWeight:700,color: isDark?'#fff':'#111'}}>Change Password</h3>
                        <p style={{margin:0,fontSize:11,color:'#888',marginTop:2}}>Update your login password</p>
                      </div>
                    </div>
                    <button onClick={() => { setShowChangePwd(false); setPwdForm({current:'',newPwd:'',confirm:''}); }}
                      style={{width:32,height:32,borderRadius:10,border:'none',cursor:'pointer',background: isDark?'rgba(255,255,255,0.08)':'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center',color:'#888'}}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{width:16,height:16}}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  {/* Form */}
                  <div style={{padding:24}}>
                    {[
                      {key:'current', label:'Current Password', placeholder:'Enter your current password',
                        icon:<svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>},
                      {key:'newPwd', label:'New Password', placeholder:'Min 8 characters',
                        icon:<svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>},
                      {key:'confirm', label:'Confirm New Password', placeholder:'Repeat new password',
                        icon:<svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>},
                    ].map(field => (
                      <div key={field.key} style={{marginBottom:16}}>
                        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:600,color:'#888',marginBottom:6,letterSpacing:'0.04em'}}>
                          {field.icon} {field.label}
                        </label>
                        <div style={{position:'relative'}}>
                          <input
                            type={showPwd[field.key] ? 'text' : 'password'}
                            placeholder={field.placeholder}
                            value={pwdForm[field.key]}
                            onChange={e => setPwdForm({...pwdForm,[field.key]:e.target.value})}
                            onKeyDown={e => e.key==='Enter' && handleChangePassword()}
                            style={{
                              width:'100%',padding:'12px 44px 12px 14px',borderRadius:12,fontSize:13,
                              border: isDark?'1px solid rgba(255,255,255,0.12)':'1px solid #e2e8f0',
                              background: isDark?'rgba(255,255,255,0.06)':'#f8fafc',
                              color: isDark?'#fff':'#111',outline:'none',
                              boxSizing:'border-box',transition:'border 0.2s'
                            }}
                          />
                          <button onClick={() => setShowPwd(p => ({...p,[field.key]:!p[field.key]}))}
                            style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',
                              background:'none',border:'none',cursor:'pointer',color:'#888',padding:4}}>
                            {showPwd[field.key]
                              ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                              : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                          </button>
                        </div>
                      </div>
                    ))}
                    <div style={{display:'flex',gap:10,marginTop:8}}>
                      <button onClick={() => { setShowChangePwd(false); setPwdForm({current:'',newPwd:'',confirm:''}); }}
                        style={{flex:1,padding:'12px',borderRadius:12,border: isDark?'1px solid rgba(255,255,255,0.12)':'1px solid #e2e8f0',
                          background:'transparent',color: isDark?'#ccc':'#666',fontWeight:600,fontSize:13,cursor:'pointer'}}>
                        Cancel
                      </button>
                      <button onClick={handleChangePassword} disabled={pwdLoading}
                        style={{flex:2,padding:'12px',borderRadius:12,border:'none',
                          background:'linear-gradient(135deg,#0d9488,#2563eb)',
                          color:'white',fontWeight:700,fontSize:13,cursor:'pointer',
                          opacity: pwdLoading?0.7:1,boxShadow:'0 4px 16px rgba(13,148,136,0.3)'}}>
                        <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                          {pwdLoading
                            ? <><svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16,animation:'spin 1s linear infinite'}}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg> Updating...</>
                            : <><svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/><circle cx="12" cy="16" r="1" fill="white"/></svg> Update Password</>}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Card>
              <Row label="Last seen"            sub="Who can see when you were last active"    right={<Sel value={lastSeen}  onChange={setLastSeen}><option>Everyone</option><option>Nobody</option></Sel>}/>
              <Row label="Online status"        sub="Who can see your live presence"           right={<Sel value={onlineVis} onChange={setOnlineVis}><option>Everyone</option><option>Nobody</option></Sel>}/>
              <Row label="Read receipts"        sub="Others won't see when you've read"        right={<Toggle on={readReceiptsP} onChange={setReadReceiptsP}/>}/>
              <Row label="Profile photo"        sub="Who can see your profile picture"         right={<Sel value={photoVis} onChange={setPhotoVis}><option>Everyone</option><option>Nobody</option></Sel>}/>
            </Card>
            <Card>
              <div style={{padding:'14px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                <div>
                  <span style={{display:'block',fontSize:13,fontWeight:500,color:'var(--row-text)'}}>End-to-end encryption</span>
                  <span style={{display:'block',fontSize:11,color:'var(--row-sub)',marginTop:2}}>AES-256 · All messages encrypted at rest</span>
                </div>
                <span style={{fontSize:10.5,fontWeight:600,padding:'3px 9px',borderRadius:20,background:'#f0fdf4',color:'#15803d',flexShrink:0}}>Active</span>
              </div>
            </Card>
            <Card>
              <Row label="Blocked users" sub="Manage who can't message you" right={<span style={{fontSize:12,color:'var(--row-sub)'}}>0 ›</span>}/>
              <Row label="Two-factor authentication" sub="Add extra login security"
                right={<span style={{fontSize:10.5,fontWeight:600,padding:'3px 9px',borderRadius:20,background:'#fef2f2',color:'#dc2626'}}>Off</span>}/>
            </Card>
          </>}

          {/* ═══ STORAGE ═══ */}
          {tab === 'storage' && <>
            <Card>
              <div style={{display:'flex',gap:10,padding:'14px 16px 10px'}}>
                {[['84 MB','Messages'],['312 MB','Files & media'],['396 MB','Total used']].map(([v,l]) => (
                  <div key={l} style={{flex:1,background:isDark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.04)',borderRadius:10,padding:'10px 12px',textAlign:'center'}}>
                    <div style={{fontSize:17,fontWeight:600,color:'var(--row-text)',letterSpacing:'-0.5px'}}>{v}</div>
                    <div style={{fontSize:10,color:'var(--row-sub)',marginTop:2}}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{padding:'0 16px 6px',fontSize:11,color:'var(--row-sub)'}}>Storage breakdown</div>
              <div style={{margin:'0 16px 6px',height:6,borderRadius:3,background:isDark?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.08)',overflow:'hidden',display:'flex'}}>
                <div style={{width:'21%',height:'100%',background:'#0d9488'}}/>
                <div style={{width:'79%',height:'100%',background:'#2563eb'}}/>
              </div>
              <div style={{display:'flex',gap:16,padding:'4px 16px 14px'}}>
                {[['#0d9488','Messages'],['#2563eb','Files & media']].map(([c,l]) => (
                  <div key={l} style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:'var(--row-sub)'}}>
                    <div style={{width:8,height:8,borderRadius:2,background:c,flexShrink:0}}/>{l}
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <Row label="Clear cached media"   sub="Frees up local storage"            right={<button style={btnStyle(isDark)} onClick={() => alert('Cache cleared!')}>Clear</button>}/>
              <Row label="Export chat history"  sub="Download as ZIP archive"           right={<button style={btnStyle(isDark)}>Export</button>}/>
              <Row label="Delete all local data" sub="Removes cache and settings"       right={<button style={{...btnStyle(isDark),borderColor:'#fca5a5',color:'#dc2626'}} onClick={() => { if(window.confirm('Delete all local Qtalk data?')) { localStorage.clear(); alert('Done. Please refresh.'); }}}>Delete</button>}/>
            </Card>
            <Card>
              <Row label="Data saver mode" sub="Reduce quality on metered networks" right={<Toggle on={dataSaver} onChange={setDataSaver}/>}/>
            </Card>
          </>}

          {/* ═══ ACCESSIBILITY ═══ */}
          {tab === 'accessibility' && <>
            <Card>
              <Row label="Reduce motion"    sub="Limit animations and transitions"      right={<Toggle on={reduceMotion}  onChange={setReduceMotion}/>}/>
              <Row label="High contrast"    sub="Increase text and border contrast"     right={<Toggle on={highContrast}  onChange={setHighContrast}/>}/>

              <Row label="Keyboard shortcuts" sub="View all available shortcuts"        right={<span style={{fontSize:13,color:'var(--row-sub)'}}>›</span>}/>
            </Card>
          </>}

          {/* ═══ ABOUT ═══ */}
          {tab === 'about' && <>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'16px 0 8px'}}>
              <div style={{width:56,height:56,borderRadius:16,background:'linear-gradient(135deg,#0d9488,#2563eb)',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:26,height:26}}>
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
              </div>
              <div style={{fontSize:17,fontWeight:700,color:'var(--row-text)',letterSpacing:'-0.4px'}}>Qtalk</div>
              <div style={{fontSize:12,color:'var(--row-sub)',marginTop:3}}>Internal Communications · QualTechEdge</div>
              <span style={{marginTop:8,fontSize:10.5,fontWeight:600,padding:'3px 10px',borderRadius:20,background:'#eff6ff',color:'#1d4ed8'}}>v 1.2.0</span>
            </div>
            <Card>
              {[['Version','1.2.0 (build 2026.04.03)'],['Server','qtalk.qualtechedge.in'],['Encryption','AES-256-CBC'],['Licence','Proprietary · QualTechEdge']].map(([k,v]) => (
                <Row key={k} label={k} right={<span style={{fontSize:12,color:'var(--row-sub)'}}>{v}</span>}/>
              ))}
            </Card>
          </>}

        </div>
      </div>
    </div>
  );
}

function btnStyle(isDark) {
  return {
    fontSize:12,fontWeight:500,padding:'6px 14px',borderRadius:8,
    border:`0.5px solid ${isDark?'rgba(255,255,255,0.15)':'rgba(0,0,0,0.15)'}`,
    background:'transparent',color:'var(--row-text)',cursor:'pointer',
  };
}
